/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {BUSINESS_PLAN, DISMISS_MSG, FCORG_BLACKLIST, GMTVP, TVP, VIEW_AUTHORITY} from '../../../common/constants';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {
  PropulsionScenarioSelectComponent
} from '../../../core/components/propulsion-scenario-select/propulsion-scenario-select.component';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {SelectableOptions} from '../../../models/selectable-options';
import {TvpVehicleProgram} from '../../../models/tvp-vehicle-program.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {FcorgService} from '../../../services/fcorg.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-create-propulsion-scenario',
    templateUrl: './create-propulsion-scenario.component.html',
    styleUrl: './create-propulsion-scenario.component.scss',
    standalone: false
})
export class CreatePropulsionScenarioComponent implements OnInit {
  protected readonly VIEW_AUTHORITY = VIEW_AUTHORITY;
  @ViewChild('propulsionScenarioSelect') propulsionScenarioSelect: PropulsionScenarioSelectComponent;
  readonly loading = new LoadingCounter();

  displayedData: PropulsionScenario[];
  selectedPropulsionScenario: PropulsionScenario;
  fcorgs: string[];
  selectedFcorg: string;
  assumptionMaxLength = 150;
  nameMaxLength = 100;
  validFcorgs: string[] = [];

  fromScenario = 'From Existing Scenario';
  fromPortfolio = 'From Portfolio';
  fromTemplate = 'From Template';

  sourceList = [this.fromScenario, this.fromPortfolio, this.fromTemplate];

  scenarioName: string;
  assumption: string;
  selectedSource: string;
  isTVP: boolean = false;

  architectures: string[];
  selectedArchitecture: string;
  vehiclePrograms: TvpVehicleProgram[] = [];
  selectedVehiclePrograms: TvpVehicleProgram[] = [];

  // By Template
  file: File;
  sheets: SelectableOptions = new SelectableOptions();

  vehicleProgramColumnDefinition: ColDef[] = [
    {headerName: 'Market Division', field: 'mktDiv', filter: AutocompleteFilterComponent},
    {headerName: 'Brand', field: 'brand', filter: AutocompleteFilterComponent},
    {headerName: 'Program Code', field: 'prgmCode', filter: AutocompleteFilterComponent},
    {headerName: 'Style Code', field: 'styleCode', filter: AutocompleteFilterComponent},
    {headerName: 'Body Style', field: 'bodyStyle', filter: AutocompleteFilterComponent},
    {headerName: 'Product Location', field: 'productLocation', filter: AutocompleteFilterComponent},
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false
  };

  constructor(public snackBar: FabMessageService,
              public stateService: StateService,
              private readonly propulsionScenarioService: PropulsionScenarioService,
              private readonly fcorgService: FcorgService,
              public readonly excelService: ExcelExportService,
              readonly router: Router,
              public readonly dialog: MatDialog) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit() {
    this.isTVP = false;
    this.selectedSource = this.fromScenario;
    this.getFcorgs();
    this.getArchitectures();
  }

  /**
   * Gets the fcorgs the user has access to
   */
  getFcorgs() {
    this.loading.track(this.fcorgService.getFcorgsById(this.stateService.getUserGmid())).subscribe(
      {
        next: (fcorgs) => {
          this.fcorgs = fcorgs.filter((fcorg: string) => !(FCORG_BLACKLIST.includes(fcorg) || fcorg === GMTVP));
        },
        error: (error) => {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
      }
    );
  }

  /**
   * Gets the architectures for creating a TVP scenario
   */
  getArchitectures(): void {
    this.loading.track(this.propulsionScenarioService.getTvpArchitectures()).subscribe(
      data => {
        this.architectures = data;
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * gets the vehicle programs to display based on the architecture selected
   * @param architecture the architecture to get the programs by
   */
  getVehiclePrograms(architecture: string): void {
    this.loading.track(this.propulsionScenarioService.getTvpVehiclePrograms(architecture)).subscribe(
      data => {
        this.vehiclePrograms = data;
      }, error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        this.selectedVehiclePrograms = [];
      }
    );
  }

  onProgramSelect(event: any) {
    const row: TvpVehicleProgram = event.data;
    if (!event.node.selected) {
      this.selectedVehiclePrograms = this.selectedVehiclePrograms.filter(element => element != row);
    } else {
      this.selectedVehiclePrograms.push(row);
    }
  }

  /**
   * Opens a pop-up to update the scenario name
   * @param field the current name
   * @param maxLength the max length the field can be
   */
  updateScenarioName(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Scenario Name',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.scenarioName = result.data.newValue;
      }
    });
  }

  /**
   * Opens a pop-up to update the scenario assumption
   * @param field the current assumption
   * @param maxLength the max length the field can be
   */
  updateAssumption(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Assumption',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.assumption = result.data.newValue;
      }
    });
  }

  /**
   * Resets data when the source changes
   */
  onSourceChange() {
    this.selectedPropulsionScenario = null;
    this.selectedFcorg = null;
    this.selectedArchitecture = null;
    this.selectedVehiclePrograms = [];
  }

  /**
   * Selects a propulsion scenario from
   * @param event the event emitted from the select component
   */
  onPropulsionScenarioSelect(event: any) {
    this.selectedPropulsionScenario = event.selectedScenarios[0];
  }


  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: true
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheets.selectedOptions.length === 0) {
          this.file = null;
        }
      }
    );
  }

  /**
   * Creates a propulsion scenario if there isn't already one with the same name
   */
  async createPropulsionScenario() {
    this.loading.begin();
    try {
      const duplicateName = await this.propulsionScenarioService.getPropulsionScenarioByName(this.scenarioName).toPromise();
      if (duplicateName !== null) {
        this.snackBar.displayError("There is already a scenario with that name", DISMISS_MSG);
        return;
      }

      switch (this.selectedSource) {
        case this.fromScenario:
          if (this.isTVP) {
            await this.propulsionScenarioService.createTVPScenarioFromScenario(this.scenarioName, this.selectedPropulsionScenario.id, this.stateService.getUserGmid(), this.assumption).toPromise();
          } else {
            await this.propulsionScenarioService.createScenarioFromScenario(this.scenarioName, this.selectedPropulsionScenario.id, this.stateService.getUserGmid(), this.assumption).toPromise();
          }
          break;
        case this.fromPortfolio:
          if (this.isTVP) {
            await this.propulsionScenarioService.createTVPScenarioFromPortfolio(this.scenarioName, this.assumption, this.createStringFromProgramList(this.selectedVehiclePrograms), this.stateService.getUserGmid()).toPromise();
          } else {
            await this.propulsionScenarioService.createScenarioFromPortfolio(this.scenarioName, this.assumption, this.selectedFcorg, this.stateService.getUserGmid()).toPromise();
          }
          break;
        case this.fromTemplate:
          await this.propulsionScenarioService.createScenarioFromTemplate(this.scenarioName, this.assumption, this.stateService.getUserGmid(), this.file, this.sheets.selectedOptions).toPromise();
          break;
      }
      this.snackBar.displaySuccess("Successfully created scenario", DISMISS_MSG);
      await this.router.navigate(["/"]);
    } catch (error) {
      this.snackBar.displayError(error, DISMISS_MSG);
    } finally {
      this.loading.end();
    }
  }

  clearSecondPage() {
    this.selectedSource = this.fromScenario;
    this.selectedPropulsionScenario = null;
    this.selectedArchitecture = null;
    this.selectedFcorg = null;
    this.selectedVehiclePrograms = [];
    this.file = null;
    this.sheets.selectedOptions = [];
    this.validFcorgs = this.isTVP ? [] : [GMTVP];
    this.propulsionScenarioSelect.getPropulsionScenarios();
  }

  /**
   * Logic to determine if the second step is complete
   */
  isSecondPageComplete() {
    switch (this.selectedSource) {
      case this.fromScenario:
        return this.selectedPropulsionScenario !== null && this.selectedPropulsionScenario !== undefined;
      case this.fromPortfolio:
        if (this.isTVP) {
          return this.selectedVehiclePrograms.length > 0;
        } else {
          return this.selectedFcorg !== null;
        }
      case this.fromTemplate:
        return (this.file) && this.sheets.selectedOptions.length > 0;
      default:
        return false;
    }
  }


  /**
   * creates the part of the sql query to filter results by the vehicle program
   * @param selectedVehiclePrograms a list of vehicle programs to filter the scenario by
   */
  createStringFromProgramList(selectedVehiclePrograms: TvpVehicleProgram[]): string {
    let listString = '';
    const endString = ' OR ';
    for (const item of selectedVehiclePrograms) {
      listString += `(MKTDIV='${
        item.mktDiv}' AND BRAND='${
        item.brand}' AND PRGMCODE='${
        item.prgmCode}' AND STYLECODE='${
        item.styleCode}' AND BODYSTYLE='${
        item.bodyStyle}') OR `;
    }
    return listString.substring(0, listString.length - endString.length);
  }

  protected readonly BUSINESS_PLAN = BUSINESS_PLAN;
  protected readonly TVP = TVP;
}
